/* eslint-disable @typescript-eslint/camelcase */
import {
  Actor,
  ClientDetail,
  ClientEntities,
  ClientMappingList,
  ClientParentData,
  ClientPartnerData,
  RequestApiClientMappingList
} from "@/domain/entities/Client";
import {
  Action,
  getModule,
  Module,
  Mutation,
  VuexModule
} from "vuex-module-decorators";
import store from "@/store";
import { Pagination } from "@/domain/entities/Pagination";
import { ClientPresenter } from "../presenters/ClientPresenter";
import { container } from "tsyringe";
import { MainAppController } from "@/app/ui/controllers/MainAppController";
import { RetryUploadClientRate } from "@/data/payload/api/ClientManagementApiRequest";
import { parsingErrorResponse } from "@/app/infrastructures/misc/Utils";
import { getHubData } from "@/app/infrastructures/misc/Cookies";
import { ReportListDownload } from "@/domain/entities/Report";

export interface ClientState {
  isLoading: boolean;
  isLoadingDetail: boolean;
  clientData: ClientEntities;
  isError: boolean;
  isErrorEdit: boolean;
  isErrorCause: string;
  openModal: boolean;
  openModalSuccess: boolean;
  actors: Actor[];
  clientDetailData: ClientDetail;
}
@Module({ namespaced: true, dynamic: true, store, name: "client" })
class ClientStore extends VuexModule implements ClientState {
  public isLoading = false;
  public isLoadingDetail = false;
  public clientData = new ClientEntities(new Pagination(1, 10), []);
  public clientDetail = "";
  public clientPartnerData: Array<ClientPartnerData> = [];
  public clientMappingData: Array<ClientMappingList> = [];
  public clientParentData: Array<ClientParentData> = [];
  public search = "";
  public filterStatus = "";
  public isError = false;
  public isErrorEdit = false;
  public isErrorCause = "";
  public openModal = false;
  public openModalSuccess = false;
  public firstRequest = true;
  public openSuccess = false;
  public isLoadingActor = false;
  public isErrorLoadActor = false;
  public actors: Actor[] = [];
  public clientDetailData: ClientDetail = new ClientDetail();
  public isLoadingClientRateBulkDownload = false;
  public clientRateBulkDownload = new ReportListDownload(
    new Pagination(1, 20),
    []
  );

  @Action
  public _onGetList(params: {
    search: string;
    status: string;
    page: number;
    limit: number;
    clientParentId?: number;
  }) {
    this.setLoading(true);
    const presenter = container.resolve(ClientPresenter);
    presenter
      .getListClient(
        params.page,
        params.limit,
        params.search,
        params.status,
        params.clientParentId
      )
      .then((res: ClientEntities) => {
        this.setClientData({
          pagination: {
            page: res.pagination.page,
            limit: params.limit
          },
          data: res.data
        });
        this.setError(false);
        this.setErrorCause("");
      })
      .catch((error: any) => {
        this.setError(true);
        this.setErrorCause(error.response ? "server" : "internet");
      })
      .finally(() => {
        this.setLoading(false);
      });
  }

  @Action
  public getDetail(id: number) {
    this.setClientDetail({
      id: id,
      clientName: "PT. Tigakarsa Tbk",
      clientCode: "VEN009001",
      clientEmail: "client@clientmail.com",
      clientAddress:
        "Jl. Puri Indah Raya No.8, RT.1/RW.8, Kembangan Sel., Kec. Kembangan, Kota Jakarta Barat, Daerah Khusus Ibukota Jakarta 11610",
      clientPriceRatesStartDate: "2020-05-05",
      clientPhone: "+6289989902010",
      clientNpwp: "99.999.999.9-999.999",
      clientVersionBasicPrice: "tigaraksa_custom_price",
      clientDiscount: "20%",
      clientVersionBasicPriceAttachment:
        "fileUploadCityRate _New_LP_12022020_1202221.xlxs",
      clientCustomPriceName: "hargaCustom_Tigaraksa_LP_jul20-des20",
      clientCustomPriceAttachment: "hargaCustom_Tigaraksa_LP_jul20-des20.xlsx",
      clientPriceRatesEndDate: "2020-06-06",
      clientRequestStatus: "rejected",
      clientPriceRatesStatus: "active",
      clientUpdatedAt: "2020-05-05, 10:00 WIB",
      clientUpdatedBy: "Bian",
      clientCreatedAt: "2020-05-05, 10:00 WIB",
      clientCreatedBy: "Bian"
    });
  }

  @Action
  public _onGetListPartner(params: {
    page: number;
    limit: number;
    search: string;
    partnerType: string;
    type?: string;
    isBranch?: boolean;
    isParent?: boolean;
    orderBy?: string;
    sortBy?: string;
    countryCode?: string;
  }) {
    this.setLoading(true);
    this.setClientPartnerData([]);
    const presenter = container.resolve(ClientPresenter);
    presenter
      .getListClientPartner(
        params.page,
        params.limit,
        params.search,
        params.partnerType,
        params.type,
        params.isBranch,
        params.isParent,
        params.orderBy,
        params.sortBy,
        params.countryCode
      )
      .then((res: ClientPartnerData[]) => {
        this.setClientPartnerData(res);
        this.setError(false);
        this.setErrorCause("");
      })
      .catch((error: any) => {
        this.setError(true);
        this.setErrorCause(error.response ? "server" : "internet");
      })
      .finally(() => {
        this.setLoading(false);
      });
  }

  @Action
  public _onGetListParent() {
    this.setLoading(true);
    this.setClientPartnerData([]);
    const presenter = container.resolve(ClientPresenter);
    presenter
      .getClientParentList()
      .then((res: ClientParentData[]) => {
        this.setClientParentData(res);
        this.setError(false);
        this.setErrorCause("");
      })
      .catch((error: any) => {
        this.setError(true);
        this.setErrorCause(error.response ? "server" : "internet");
      })
      .finally(() => {
        this.setLoading(false);
      });
  }

  @Action
  public getRTCListClientParent(transportType: string) {
    this.setLoading(true);
    this.setClientParentData([]);
    const presenter = container.resolve(ClientPresenter);
    const hubId = Number(getHubData()?.hubId || 0);
    presenter
      .getRTCListClientParent(hubId, transportType)
      .then((res: ClientParentData[]) => {
        this.setClientParentData(res);
        this.setError(false);
        this.setErrorCause("");
      })
      .catch((error: any) => {
        this.setError(true);
        this.setErrorCause(error.response ? "server" : "internet");
      })
      .finally(() => {
        this.setLoading(false);
      });
  }

  @Action
  public _onGetCLientMappingList(params: RequestApiClientMappingList) {
    this.setLoading(true);
    this.setClientMappingData([]);
    const presenter = container.resolve(ClientPresenter);
    return presenter
      .getClientMappingList(params)
      .then((res: ClientMappingList[]) => {
        this.setClientMappingData(res);
        this.setError(false);
        this.setErrorCause("");
        return res;
      })
      .catch((error: any) => {
        this.setError(true);
        this.setErrorCause(error.response ? "server" : "internet");
      })
      .finally(() => {
        this.setLoading(false);
      });
  }

  @Action
  public fetchClientList() {
    this.setFirstRequest(false);
    this._onGetList({
      search: this.search,
      status: this.filterStatus,
      page: this.clientData.pagination.page,
      limit: this.clientData.pagination.limit
    });
  }

  @Action
  public initClient() {
    this.setFirstPage();
    this.setFirstRequest(true);
    this.setFilterStatus("");
    this.setSearch("");
    this._onGetList({
      search: "",
      status: "",
      page: this.clientData.pagination.page,
      limit: this.clientData.pagination.limit
    });
  }

  @Action
  public setFirstPage() {
    this.clientData.pagination.page = 1;
  }

  @Action
  public selectStatusAction(params: { name: string; value: string }) {
    this.setFilterStatus(params.value);
    this.setFirstPage();
    this.fetchClientList();
  }

  @Action
  public searchAction(value: string) {
    this.setSearch(value);
    this.setFirstPage();
    this.fetchClientList();
  }

  @Action
  public clear() {
    this.searchAction("");
  }

  @Action
  public getListOfActor(search: string) {
    this.setLoading(true);
    const presenter = container.resolve(ClientPresenter);
    presenter
      .getListOfActor(search)
      .then((res: Actor[]) => {
        this.setListActor(res);
        this.setIsErrorLoadingActor(false);
        this.setErrorCause("");
      })
      .catch((err: any) => {
        this.setIsErrorLoadingActor(true);
        this.setErrorCause(err.response ? "server" : "internet");
      })
      .finally(() => {
        this.setIsErrorLoadingActor(false);
      });
  }

  @Action
  public fetchClientDetail(clientId: number): Promise<ClientDetail> {
    this.setLoading(true);
    const presenter = container.resolve(ClientPresenter);

    return new Promise((resolve, reject) => {
      presenter
        .getClientDetail(clientId)
        .then(res => {
          this.setClientDetailData(res);
          resolve(res);
        })
        .catch(error => {
          reject(error);
        })
        .finally(() => {
          this.setLoading(false);
        });
    });
  }

  @Action
  public getClientRateBulkDownloadList(params: {
    page: number;
    limit: number;
    type: string;
    clientId: number;
    isTotalData: boolean;
  }) {
    this.setLoadingClientRateBulkDownload(true);
    const presenter = container.resolve(ClientPresenter);
    return presenter
      .getClientRateBulkDownloadList(params)
      .then((res) => {
        this.setClientRateBulkDownload(res);
      })
      .catch((err: any) => {
        MainAppController.showErrorMessage(
          parsingErrorResponse(err, "Gagal mengambil daftar download", () =>
            this.getClientRateBulkDownloadList(params)
          )
        );
        this.setClientRateBulkDownload(
          new ReportListDownload(new Pagination(1, 20), [])
        );
      })
      .finally(() => {
        this.setLoadingClientRateBulkDownload(false);
      });
  }

  @Action
  public getBulkErrorReport(payload: { id: number; filename: string }) {
    const { id, filename } = payload;
    MainAppController.showLoading();
    MainAppController.closeErrorMessage();
    const presenter = container.resolve(ClientPresenter);

    presenter
      .getBulkErrorReport(id)
      .then((res: any) => {
        // 1. Use the backend data directly.
        // If it's already a Blob (recommended), use it. Otherwise, wrap it generically.
        const fileBlob = res.data instanceof Blob ? res.data : new Blob([res.data]);
        // 2. Set up a default fallback filename
        let exportFilename = filename;
        // 3. Extract the real filename from the Content-Disposition header
        const disposition =
          res.headers?.["content-disposition"] ||
          res.headers?.get?.("content-disposition");
        if (disposition && disposition.includes("filename=")) {
          // Regex to extract filename safely whether it uses quotes or not
          const filenameRegex = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/;
          const matches = filenameRegex.exec(disposition);
          if (matches != null && matches[1]) {
            exportFilename = matches[1].replace(/['"]/g, "");
          }
        }

        // 4. Pure download handling without forcing a file type
        const nav: any = window.navigator;
        if (nav && typeof nav.msSaveOrOpenBlob === "function") {
          nav.msSaveOrOpenBlob(fileBlob, exportFilename);
        } else {
          const blobUrl = window.URL.createObjectURL(fileBlob);
          const link = document.createElement("a");
          link.style.display = "none";
          link.href = blobUrl;
          link.setAttribute("download", exportFilename);
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          // Clean up the URL object to prevent memory leaks
          setTimeout(() => window.URL.revokeObjectURL(blobUrl), 1000);
        }

        this.setOpenModalSuccess(true);
      })
      .catch((err) => {
        this.setOpenModalSuccess(false);
        MainAppController.showErrorMessage(
          parsingErrorResponse(err, "Download Gagal!", () =>
            this.getBulkErrorReport({ id, filename })
          )
        );
      })
      .finally(() => {
        MainAppController.closeLoading();
      });
  }

  @Mutation
  public setLoading(isLoading: boolean) {
    this.isLoading = isLoading;
  }

  @Mutation
  public setClientData(data: ClientEntities) {
    this.clientData = data;
  }

  @Mutation
  public setClientPartnerData(data: ClientPartnerData[]) {
    this.clientPartnerData = data;
  }

  @Mutation
  public setClientParentData(data: ClientParentData[]) {
    this.clientParentData = data;
  }

  @Mutation
  public setClientMappingData(data: ClientMappingList[]) {
    this.clientMappingData = data;
  }

  @Mutation
  private setClientDetail(data: any) {
    this.clientDetail = data;
  }

  @Mutation
  private setSearch(value: string) {
    this.search = value;
  }

  @Mutation
  private setFilterStatus(value: string) {
    this.filterStatus = value;
  }

  @Mutation
  private setError(boolean: boolean) {
    this.isError = boolean;
  }

  @Mutation
  public setErrorEdit(boolean: boolean) {
    this.isErrorEdit = boolean;
  }

  @Mutation
  public setErrorCause(str: string) {
    this.isErrorCause = str;
  }

  @Mutation
  public setOpenModal(value: boolean) {
    this.openModal = value;
  }

  @Mutation
  public setOpenModalSuccess(value: boolean) {
    this.openModalSuccess = value;
  }

  @Mutation
  public setFirstRequest(value: boolean) {
    this.firstRequest = value;
  }

  @Action
  public handleError() {
    this.setError(false);
  }

  @Mutation
  public setOpenSuccess(value: boolean) {
    this.openSuccess = value;
  }

  @Mutation
  public setIsLoadingActor(value: boolean) {
    this.isLoadingActor = value;
  }

  @Mutation
  public setIsErrorLoadingActor(value: boolean) {
    this.isErrorLoadActor = value;
  }

  @Mutation
  public setListActor(value: Actor[]) {
    this.actors = value;
  }

  @Mutation
  public setLoadingClientRateBulkDownload(val: boolean) {
    this.isLoadingClientRateBulkDownload = val;
  }

  @Mutation
  public setClientRateBulkDownload(val: ReportListDownload) {
    this.clientRateBulkDownload = val;
  }

  @Action
  public async retryUploadClientRate(params: {
    clientId: number;
    rateVersionId: number;
  }) {
    MainAppController.showLoading();
    MainAppController.closeErrorMessage();
    const presenter = container.resolve(ClientPresenter);
    return presenter
      .retryUploadClientRate(
        new RetryUploadClientRate(params.clientId, params.rateVersionId)
      )
      .then(() => {
        return true;
      })
      .catch((err: any) => {
        MainAppController.showErrorMessage(parsingErrorResponse(err, ""));
        return false;
      })
      .finally(() => {
        MainAppController.closeLoading();
      });
  }

  @Mutation
  public setClientDetailData(data: ClientDetail): void {
    this.clientDetailData = data;
  }
}

export const ClientController = getModule(ClientStore);
